import { LabelConfig } from '@telespot/domain';

/**
 * Decides which label configuration is authoritative when more than one exists
 * for the same (label, method type) pair, and projects a flat list of rows onto
 * the axis its caller reads along.
 *
 * Duplicates should not be created — the save command upserts — but rows written
 * before that, or by a racing writer, can still exist. Without this rule each read
 * path picks whichever row the repository happened to return first, an order no
 * repository contract specifies, so the two protocol editors can show different
 * colours for the same pair.
 */
export class LabelConfigProjector {
  /**
   * Configurations keyed by method type id, for a read that holds ONE LABEL
   * across many method types (the label-usage query).
   */
  public static groupByMethodType(configs: LabelConfig[]): Map<string, LabelConfig> {
    return this.pickWinners(configs, (config) => config.methodTypeId);
  }

  /**
   * Colours keyed by label uuid, for a read that holds ONE METHOD TYPE across
   * many labels (the method-type colour query).
   */
  public static colorsByLabel(configs: LabelConfig[]): Map<string, string> {
    const winners = this.pickWinners(configs, (config) => config.label);
    return new Map(Array.from(winners, ([labelUuid, config]) => [labelUuid, config.color]));
  }

  private static pickWinners(
    configs: LabelConfig[],
    keyOf: (config: LabelConfig) => string
  ): Map<string, LabelConfig> {
    const winners = new Map<string, LabelConfig>();

    for (const config of configs) {
      const key = keyOf(config);
      const incumbent = winners.get(key);
      if (!incumbent || this.supersedes(config, incumbent)) winners.set(key, config);
    }

    return winners;
  }

  /**
   * Most recently updated wins; creation time breaks a tie on update time. When
   * both timestamps match, the greater id wins — not because ids carry meaning,
   * but so the outcome cannot depend on the order the repository returned the
   * rows in.
   */
  private static supersedes(candidate: LabelConfig, incumbent: LabelConfig): boolean {
    const byUpdate = this.millis(candidate.updatedAt) - this.millis(incumbent.updatedAt);
    if (byUpdate !== 0) return byUpdate > 0;

    const byCreation = this.millis(candidate.createdAt) - this.millis(incumbent.createdAt);
    if (byCreation !== 0) return byCreation > 0;

    return (candidate.id ?? '') > (incumbent.id ?? '');
  }

  /**
   * Every stored configuration carries both timestamps — the reusable repository
   * specification asserts it for the database and the mock alike. The coalesce is
   * here only because the entity types them optional for the not-yet-saved case,
   * which never reaches this service.
   */
  private static millis(timestamp: Date | undefined): number {
    return timestamp?.getTime() ?? 0;
  }
}
